/**
 * 제품 목록 — 탭 둘: 전체 · 미연결. 실데이터(백엔드 /api/admin/products, 2026-10-14 — 시안 2026-10-13 을 실데이터로).
 * product 한 줄 = hclt_product 한 행. 실무 제품만 보인다 — 견적이 자동으로 만든 구성 product 는 서버가 거른다(is_draft).
 * 탭 · 검색 · 필터 셋은 서버가 거른다(시안의 클라이언트 필터는 퇴역). 카테고리 선택지는 SPEC_CATEGORIES 고정, 제조사는 서버가 실무 제품 전체의 distinct 를 한 번 준다(QA 2026-10-20 #10 — 받은 500줄에서 긁으면 쪽마다 달랐다).
 * 검색어 · 필터 · 쪽은 주소에 **남기지 않는다** — 새로고침하면 비운다(회의 2026-10-21 · 총괄 "상세는 새 탭이라 뒤로가기가 없다, 날려라". QA 2026-10-20 #9 의 유지를 뒤집음). 처음 열 때 주소의 ?q=… 는 읽는다(깊은 링크).
 * 열 · 탭 · 필터 · 버튼은 hcltech-parts `docs/PRODUCT_SCREENS.md` §1 그대로. 줄 클릭 = 제품 상세(새 탭). 긁어 복사(드래그)면 안 연다 — lib/selection 드래그 가드.
 */

import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import type { EProductTab, ProductListQuery, ProductView, ProductViewList } from '../api/client';
import { findMenuPlace, menuHeading, renderMenuTabs } from '../components/menu';
import { notesPopAt, renderNotesButton } from '../components/notes-pop';
import type { NotesPop } from '../components/notes-pop';
import { renderPagerLine } from '../components/pager-line';
import { assert } from '../lib/assert';
import { escapeHtml, fmtDate } from '../lib/cells';
import { hasTextSelection } from '../lib/selection';
import { refreshKeepingFocus } from '../lib/keep-focus';
import { SPEC_CATEGORIES } from '@hcltech/shared/server-config/spec-category';
import { ROW_NO_HEAD, rowNoCell } from '../components/row-no';

interface ProductListData {
  query: ProductListQuery;          // 탭 · 검색 · 필터 셋 — 서버가 거른다
  list: ProductViewList | null;     // 받은 묶음. null = 아직
  vendors: string[];                // 제조사 셀렉트의 선택지(서버, 페이지 열 때 한 번). [] = 아직
  loading: boolean;
  error: string;
  notesPop: NotesPop | null;        // 열린 메모 창. null = 닫힘
}

const TAB_HREF: Record<EProductTab, string> = { all: '/products', unlinked: '/products/unlinked' };
const QUERY_DEBOUNCE_MS = 300;      // 글자마다 서버를 안 부른다

/** URL 의 ?q=&category=&vendor=&offset= → 목록 질의. 없는 키는 기본값(전체 · 첫 쪽). 음수 offset 은 기본값으로(주소는 밖에서 온 값). 옛 ?type= 은 읽지 않는다(2026-10-29 구성 — 목록엔 단품만) */
export function productListQueryOf(tab: EProductTab, search: URLSearchParams): ProductListQuery {
  const offset = Number(search.get('offset') ?? '0');
  return {
    tab,
    q: search.get('q') ?? '',
    category: search.get('category') ?? '',
    vendor: search.get('vendor') ?? '',
    offset: Number.isInteger(offset) && offset > 0 ? offset : 0,
  };
}

/** 목록 질의 → URL 의 검색 글(?부터). 기본값 칸은 안 적는다 — 전부 기본이면 '' */
export function productListSearchOf(query: ProductListQuery): string {
  const params = new URLSearchParams();
  if (query.q !== '') params.set('q', query.q);
  if (query.category !== '') params.set('category', query.category);
  if (query.vendor !== '') params.set('vendor', query.vendor);
  if (query.offset !== 0) params.set('offset', String(query.offset));
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

export class ProductListView extends SolaView<ProductListData> {
  override events = {
    input:   { '@query': 'handleQuery', '@notes-input': 'handleNotesInput' },
    keydown: { '@notes-input': 'handleNotesKey' },   // Esc 닫기
    change:  { '@category-filter': 'handleCategoryFilter', '@vendor-filter': 'handleVendorFilter' },
    // 메모 넷이 줄 열기(open)보다 앞 — Sola 는 먼저 맞는 것 하나만 부른다. 창 안을 눌러도 줄 열기로 안 넘어간다(notes-pop)
    click:   { '@notes-save': 'handleNotesSave', '@notes-close': 'handleNotesClose', '@notes-pop': 'handleNotesPop', '@notes-toggle': 'handleNotesToggle', '@page': 'handlePage', '@open': 'handleOpen' },
  };

  private queryTimer: ReturnType<typeof setTimeout> | null = null;
  private loadSeq = 0;   // 불러오기 순번 — 늦게 온 옛 응답이 새 결과를 덮지 않게(QA 2026-10-20 #3 응답 경합)

  constructor(params: { tab: EProductTab; search: URLSearchParams }) {   // search = 주소의 ?… — 새로고침 · 뒤로가기에도 검색어 · 필터 · 쪽이 남는다(QA 2026-10-20 #9)
    super();
    this.data = { query: productListQueryOf(params.tab, params.search), list: null, vendors: [], loading: true, error: '', notesPop: null };
  }

  override getTitle(): string {
    return '제품 목록 — HCLTech';
  }

  override onMount(): void {
    void this.load();
    void this.loadVendors();
  }

  render(): HTMLElement {
    const place = findMenuPlace(TAB_HREF[this.data.query.tab]);
    assert(place !== null, `제품 목록이 메뉴에 없다: ${TAB_HREF[this.data.query.tab]} — MENU_GROUPS 제품 목록의 탭에 있어야 한다`);
    return htmlSolaWithInstance(this)`
      <div class="page page-products pv-page">
        <header class="page-header">
          <h1>${menuHeading(place)}</h1>
          ${renderMenuTabs(place)}
          <span class="pv-spacer"></span>
          <a class="btn-primary" data-link href="/products/new">제품 등록</a>
        </header>
        <section class="page-body pv-body">${this.renderBody()}</section>
      </div>`;
  }

  /** 본문(.pv-body)을 다시 그린다 — 찾기 칸에 치던 포커스 · 커서는 지킨다(lib/keep-focus, 2026-10-28) */
  private refreshBody(): void {
    refreshKeepingFocus(this._rootElement ?? null, '.pv-body', () => this.refreshRegion('.pv-body', () => this.renderBody()));
  }

  // ─── 불러오기 ───
  /** 지금 query 로 한 묶음. 실패면 error 글. 끝나면 본문 다시 그림 */
  private async load(): Promise<void> {
    const seq = ++this.loadSeq;
    this.data = { ...this.data, loading: true, error: '', notesPop: null };
    history.replaceState(null, '', TAB_HREF[this.data.query.tab]);   // 주소는 탭까지만 — 검색어 · 필터 · 쪽은 새로고침 때 비운다(회의 2026-10-21)
    this.refreshBody();
    try {
      const list = await api.fetchProducts(this.data.query);
      if (seq !== this.loadSeq) return;   // 그 사이 새 불러오기가 시작됐다 — 이 응답은 버린다
      this.data = { ...this.data, list, loading: false };
    } catch (e) {
      if (seq !== this.loadSeq) return;
      this.data = { ...this.data, loading: false, error: e instanceof Error ? e.message : String(e) };
    }
    this.refreshBody();
  }

  /** 제조사 선택지 — 한 번. 실패해도 목록은 그대로(셀렉트만 빈다 — 고른 값은 withCurrent 가 남긴다) */
  private async loadVendors(): Promise<void> {
    try {
      const vendors = await api.fetchProductVendors();
      this.data = { ...this.data, vendors };
      this.refreshBody();
    } catch (e) {
      console.error('제조사 선택지를 못 받았다', e);
    }
  }

  private renderBody(): string {
    return `${this.renderFilters()}${this.renderTable()}`;
  }

  // ─── 필터 ───
  private renderFilters(): string {
    const { query, list } = this.data;
    const option = (value: string, label: string, current: string): string => `<option value="${escapeHtml(value)}" ${current === value ? 'selected' : ''}>${escapeHtml(label)}</option>`;
    return `
      <div class="pc-filter-bar">
        <label class="pc-search">
          <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>
          <input type="search" data-action="query" placeholder="파트넘버 · 디스크립션 · 제품번호" value="${escapeHtml(query.q)}">
        </label>
        <select class="pc-select" data-action="category-filter" aria-label="카테고리">
          ${option('', '전체 카테고리', query.category)}${this.categoryOptions().map((category) => option(category, category, query.category)).join('')}
        </select>
        <select class="pc-select" data-action="vendor-filter" aria-label="제조사">
          ${option('', '전체 제조사', query.vendor)}${this.vendorOptions().map((vendor) => option(vendor, vendor, query.vendor)).join('')}
        </select>
        <span class="pv-spacer"></span>
        ${list === null ? '' : renderPagerLine(list, 'page')}
      </div>`;
  }

  /** 카테고리 선택지 — 21종 고정. 고른 값이 목록에 없어도 셀렉트가 안 튄다 */
  private categoryOptions(): string[] {
    return this.withCurrent([...SPEC_CATEGORIES], this.data.query.category);
  }

  /** 제조사 선택지 — 서버가 준 전체 distinct(loadVendors) */
  private vendorOptions(): string[] {
    return this.withCurrent([...this.data.vendors], this.data.query.vendor);
  }

  private withCurrent(options: string[], current: string): string[] {
    return current === '' || options.includes(current) ? options : [current, ...options];
  }

  // ─── 표 ───
  private renderTable(): string {
    const lead = this.data.query.tab === 'unlinked'
      ? '카탈로그 행이 아직 없는 제품 — 요청이 열려 있거나(요청 중) 아직 안 냈다(없음). 승격되면 전체로 돌아간다.'
      : '회사가 파는 단위 한 줄. 컨디션 열은 없다 — 실물(unit)의 축이다. 제조사 · 스페어는 연결된 카탈로그 행에서 읽는다. 검색은 옵션 · 스페어 · 어셈블리 번호 전부에 걸린다.';
    return `
      <p class="pv-lead">${lead}</p>
      <table class="pv-table pv-products">
        <thead><tr>${ROW_NO_HEAD}<th>제품번호</th><th>파트넘버</th><th>스페어 넘버</th><th>어셈블리 넘버</th><th>카테고리</th><th>제조사</th><th>디스크립션</th><th>메모</th><th>등록일</th></tr></thead>
        <tbody>${this.renderRows()}</tbody>
      </table>`;
  }

  private renderRows(): string {
    if (this.data.error !== '') return `<tr><td colspan="10" class="text-danger">${escapeHtml(this.data.error)}</td></tr>`;
    if (this.data.loading || this.data.list === null) return '<tr><td colspan="10" class="text-muted">불러오는 중…</td></tr>';
    if (this.data.list.rows.length === 0) return '<tr><td colspan="10" class="text-muted">맞는 줄이 없습니다</td></tr>';
    return this.data.list.rows.map((product, index) => this.renderRow(product, this.data.query.offset + index)).join('');
  }

  /** 줄 하나 — 이카운트 품목리스트 꼴(제품번호 · 파트넘버 · 스페어 · 어셈블리가 앞 — 어셈블리는 회의 2026-10-21 "번호 셋 다 보인다" — 타입 칸은 뺐다, 현민 2026-10-19 · 메모는 아이콘 — 누르면 창). 디스크립션은 남는 폭 전부, 넘치면 … (올리면 전체). 카테고리는 spec_category 만(서버는 —). 모델 · 카탈로그 칸은 뺐다(회의 2026-10-15 — 모델은 서버만 차고 디스크립션에 있다, 카탈로그 연결은 카탈로그 쪽 일). 단종은 목록에 안 온다(서버가 is_active 만). 줄을 누르면 상세(새 탭) */
  private renderRow(product: ProductView, no: number): string {
    return `
      <tr class="pl-row" data-action="open" data-href="/products/${escapeHtml(product.id)}" title="누르면 상세(새 탭) · 긁어 복사하면 안 열림">
        ${rowNoCell(no, 0)}
        <td class="pl-sub pl-id mono">${escapeHtml(product.hclt_number)}</td>
        <td class="pl-main pl-pn"><b class="mono">${product.option_part === null ? '—' : escapeHtml(product.option_part)}</b></td>
        <td class="pl-sub pl-pn mono">${product.spare_part === null ? '—' : escapeHtml(product.spare_part)}</td>
        <td class="pl-sub pl-pn mono">${product.assembly_part === null ? '—' : escapeHtml(product.assembly_part)}</td>
        <td class="pl-sub">${product.spec_category === null ? '—' : escapeHtml(product.spec_category)}</td>
        <td class="pl-sub">${product.vendor === null ? '—' : escapeHtml(product.vendor)}</td>
        <td class="pl-main pl-desc-cell" title="${product.description === null ? '' : escapeHtml(product.description)}"><div class="pl-clamp">${product.description === null ? '' : escapeHtml(product.description)}</div></td>
        ${this.renderNotesCell(product)}
        <td class="pl-sub pl-date"><span class="mono">${escapeHtml(fmtDate(product.created_at))}</span></td>
      </tr>`;
  }

  // ─── 메모 창 ───
  /** 메모 칸 — 아이콘 하나. 누르면 창(2026-10-19 — 글 두 줄 대신). 그리기는 components/notes-pop */
  private renderNotesCell(product: ProductView): string {
    return `
        <td class="pl-sub pl-notes">${renderNotesButton(product.id, product.notes, this.data.notesPop)}
        </td>`;
  }

  /** 받은 묶음에서 product 한 줄. 아이콘은 그려진 줄에만 있으니 없으면 있을 수 없는 상태 */
  private rowOf(productId: string): ProductView {
    const product = this.data.list === null ? undefined : this.data.list.rows.find((row) => row.id === productId);
    assert(product !== undefined, `메모 아이콘의 product ${productId} 가 받은 묶음에 없다 — 아이콘은 renderRow 가 그린 줄에만 있다`);
    return product;
  }

  private closeNotesPop(): void {
    this.data = { ...this.data, notesPop: null };
    this.refreshBody();
  }

  /** 아이콘 → 창 열기(같은 줄이면 닫기) */
  handleNotesToggle(_e: Event, el: HTMLElement): void {
    const productId = el.dataset.id;
    assert(productId !== undefined, '메모 아이콘은 data-id(product id)를 가진다 — renderNotesCell 이 붙였다');
    if (this.data.notesPop !== null && this.data.notesPop.rowId === productId) {
      this.closeNotesPop();
      return;
    }
    const product = this.rowOf(productId);
    this.data = { ...this.data, notesPop: notesPopAt(productId, product.notes === null ? '' : product.notes, el) };
    this.refreshBody();
    this._rootElement?.querySelector<HTMLTextAreaElement>('.pl-notes-pop textarea')?.focus();
  }

  /** 글 — draft 만 바꾼다. 다시 그리면 글상자 커서가 날아간다 */
  handleNotesInput(_e: Event, el: HTMLElement): void {
    const pop = this.data.notesPop;
    assert(pop !== null, '메모 글상자는 열린 창에만 있다 — renderNotesButton 이 그렸다');
    this.data = { ...this.data, notesPop: { ...pop, draft: (el as HTMLTextAreaElement).value } };
  }

  handleNotesKey(e: Event): void {
    if ((e as KeyboardEvent).key === 'Escape') this.closeNotesPop();
  }

  /** 창 안 클릭 — 줄 열기로 안 넘어가게 받기만 한다 */
  handleNotesPop(): void {
    // 일부러 비움
  }

  handleNotesClose(): void {
    this.closeNotesPop();
  }

  /** 저장 → 그 줄의 notes 만 바꿔 끼우고 닫는다. 실패면 창에 글 */
  async handleNotesSave(): Promise<void> {
    const pop = this.data.notesPop;
    assert(pop !== null, '[저장] 은 열린 창에만 있다 — renderNotesButton 이 그렸다');
    this.data = { ...this.data, notesPop: { ...pop, saving: true, error: '' } };
    this.refreshBody();
    try {
      const detail = await api.updateProductNotes(pop.rowId, pop.draft);
      const list = this.data.list;
      assert(list !== null, '메모 창은 받은 묶음의 줄에서만 열린다');
      const rows = list.rows.map((row) => row.id === pop.rowId ? { ...row, notes: detail.notes } : row);
      this.data = { ...this.data, list: { ...list, rows }, notesPop: null };
    } catch (e) {
      this.data = { ...this.data, notesPop: { ...pop, saving: false, error: e instanceof Error ? e.message : String(e) } };
    }
    this.refreshBody();
  }

  /** 줄 클릭 → 상세(새 탭). 줄 안의 링크 클릭은 그 링크가 받고, 긁어 복사(드래그)면 안 연다(회의 2026-10-21) */
  handleOpen(e: Event, el: HTMLElement): void {
    if ((e.target as HTMLElement).closest('a') !== null) return;
    if (hasTextSelection()) return;
    const href = el.dataset.href;
    assert(href !== undefined, '줄은 data-href(상세 주소)를 가진다 — renderRow 가 붙였다');
    window.open(href, '_blank', 'noopener');
  }

  // ─── 핸들러 — query 칸 하나 바꾸고 load ───
  handleQuery(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, query: { ...this.data.query, q: (el as HTMLInputElement).value, offset: 0 } };
    if (this.queryTimer !== null) clearTimeout(this.queryTimer);
    this.queryTimer = setTimeout(() => { this.queryTimer = null; void this.load(); }, QUERY_DEBOUNCE_MS);
  }

  handleCategoryFilter(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, query: { ...this.data.query, category: (el as HTMLSelectElement).value, offset: 0 } };
    void this.load();
  }

  /** 쪽 이동 — 버튼의 data-offset 으로 */
  handlePage(_e: Event, el: HTMLElement): void {
    const offset = Number(el.dataset.offset);
    assert(Number.isInteger(offset) && offset >= 0, '페이저 버튼은 data-offset(그 쪽의 offset)을 가진다 — renderPagerLine 이 붙였다');
    this.data = { ...this.data, query: { ...this.data.query, offset } };
    void this.load();
  }

  handleVendorFilter(_e: Event, el: HTMLElement): void {
    this.data = { ...this.data, query: { ...this.data.query, vendor: (el as HTMLSelectElement).value, offset: 0 } };
    void this.load();
  }
}
